import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  AuthContext,
  Icon,
  Loader,
  Logo,
  useAPI,
  ViewContext
} from "@/components/lib";
import axios from "axios";
import { motion } from "framer-motion";
import defaultPlayerImage from "@/images/default_american_football.png";
import PasswordModal from "./modals/password";
import DeactivateModal from "./modals/deactivate";
import ActivateModal from "./modals/activate";
import DeleteAccountModal from "./modals/delete";
import VerifyEmailModal from "./modals/verifyEmail";
import Styles from "@/css/mainStyles";

export default function PlayerSettings() {
  const viewContext = useContext<any>(ViewContext);
  const authContext = useContext<any>(AuthContext);
  const { user } = authContext;
  const { data: player, loading: playerLoading } = useAPI(
    `/api/player/profile?profileName=${user?.profileName}`
  );
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [isReactivateOpen, setIsReactivateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [imageError, setImageError] = useState<boolean>(false);

  const imgURL = "https://ghs-general.s3.us-east-1.amazonaws.com/";
  const profile_image = imgURL + player?.profile_pic;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm();

  useEffect(() => {
    if (player) {
      setValue("email", player.email);
    }
  }, [player, setValue]);

  const onEmailSubmit = async (data: any) => {
    const url = `/api/player/${user.id}`;

    try {
      await axios({
        method: "patch",
        url,
        data: { email: data.email }
      });
      setIsEmailOpen(true);
      viewContext.notification.show("Email verification sent", "success");
    } catch (error) {
      console.error(error);
      viewContext.notification.show(error.message, "error");
    }
  };

  if (playerLoading)
    return (
      <div className="w-full flex justify-center pt-10">
        <Loader />
      </div>
    );

  if (!player) {
    return (
      <div className="text-center text-xl w-full text-white pt-4">
        Player not found
      </div>
    );
  } else {
    return (
      <div className="pb-10 md:flex gap-4">
        <div className="md:w-[48%]">
          <div className="relative w-full overflow-hidden shadow-lg min-h-[300px]">
            {!imageError && player?.profile_pic ? (
              <img
                src={profile_image}
                alt="Grapevine High School"
                className="w-full h-[450px] md:h-[550px] lg:h-[600px] object-cover overflow-hidden"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <img
                  src={defaultPlayerImage}
                  alt="Grapevine High School"
                  className="w-full lg:w-3/5 h-[300px] md:h-[400px] lg:h-full"
                />
              </div>
            )}

            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(7, 16, 27, 0) 50%, #07101B 100%)"
              }}
            />
            <a
              href={"/" + user.profileName}
              className="rounded-full bg-white p-2 absolute top-8 left-6"
            >
              <Icon
                size={20}
                image="arrow-left"
                className="text-blue-600 w-6 h-6"
              />
            </a>
          </div>
        </div>
        <div className="space-y-4 font-bold px-2 md:px-4 md:w-[52%]">
          <h1 className="text-white text-2xl md:text-4xl font-bold ml-5 mt-2">
            Account Settings
          </h1>
          <div className="flex flex-col gap-4 md:gap-8 mt-4 px-6">
            <form onSubmit={handleSubmit(onEmailSubmit)} className="space-y-2">
              <label className={Styles.label}>Email</label>
              <div className="flex gap-2 md:gap-4">
                <input
                  placeholder="Email@mail.com"
                  type="email"
                  className={Styles.input}
                  {...register("email", { required: true })}
                  style={errors.email && { border: "1px solid red" }}
                />
                <button type="submit" className={Styles.button2}>
                  Update
                </button>
              </div>
            </form>

            <div className="flex flex-col">
              <p className={Styles.label}>Change Password</p>
              <p className="text-sm ml-2 mb-2 md:text-lg">
                Click the button below to update your password for enhanced
                security.
              </p>
              <button
                type="button"
                className={Styles.button}
                onClick={() => setIsPasswordOpen(true)}
              >
                Change Password
              </button>
            </div>

            <div className="flex flex-col">
              <p className={Styles.label}>Account Management</p>
              <p className="text-sm ml-2 mb-2 md:text-lg">
                Please choose an option below if you wish to deactivate or
                permanently delete your account.
              </p>
              <div className="flex gap-2 md:gap-4 mt-2 w-full justify-evenly">
                {player.disabled ? (
                  <button
                    type="button"
                    className="bg-blue-600 p-2 md:p-3 text-sm md:text-lg text-white flex justify-center w-full md:px-6 items-center gap-4 shadow-md rounded-full"
                    onClick={() => setIsReactivateOpen(true)}
                  >
                    Reactivate Account
                  </button>
                ) : (
                  <button
                    type="button"
                    className="bg-white p-2 md:p-3 text-sm md:text-lg text-black flex justify-center w-full md:px-6 items-center gap-4 shadow-md rounded-full"
                    onClick={() => setIsDeactivateOpen(true)}
                  >
                    Deactivate Account
                  </button>
                )}
                <button
                  type="button"
                  className="bg-transparent p-2 md:p-3 text-sm md:text-lg text-white border border-white flex justify-center w-full md:px-6 items-center gap-4 shadow-md rounded-full"
                  onClick={() => setIsDeleteOpen(true)}
                >
                  Delete Account
                </button>
              </div>
            </div>

            <Logo className="w-48 lg:w-64 mx-auto mt-5 lg:hidden" />

            {isPasswordOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center md:justify-center">
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="bg-white w-full p-6 rounded-t-2xl shadow-lg md:hidden"
                >
                  <PasswordModal setOpen={setIsPasswordOpen} />
                </motion.div>

                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="hidden md:block bg-white w-full md:w-1/3 p-6 rounded-2xl shadow-lg"
                >
                  <PasswordModal setOpen={setIsPasswordOpen} />
                </motion.div>
              </div>
            )}

            {isDeactivateOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center md:justify-center">
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="bg-white w-full p-6 rounded-t-2xl shadow-lg md:hidden"
                >
                  <DeactivateModal id={user.id} setOpen={setIsDeactivateOpen} />
                </motion.div>

                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="hidden md:block bg-white w-full md:w-1/3 p-6 rounded-2xl shadow-lg"
                >
                  <DeactivateModal id={user.id} setOpen={setIsDeactivateOpen} />
                </motion.div>
              </div>
            )}

            {isReactivateOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center md:justify-center">
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="bg-white w-full p-6 rounded-t-2xl shadow-lg md:hidden"
                >
                  <ActivateModal id={user.id} setOpen={setIsReactivateOpen} />
                </motion.div>

                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="hidden md:block bg-white w-full md:w-1/3 p-6 rounded-2xl shadow-lg"
                >
                  <ActivateModal id={user.id} setOpen={setIsReactivateOpen} />
                </motion.div>
              </div>
            )}

            {isDeleteOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center md:justify-center">
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="bg-white w-full p-6 rounded-t-2xl shadow-lg md:hidden"
                >
                  <DeleteAccountModal id={user.id} setOpen={setIsDeleteOpen} />
                </motion.div>

                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="hidden md:block bg-white w-full md:w-1/3 p-6 rounded-2xl shadow-lg"
                >
                  <DeleteAccountModal id={user.id} setOpen={setIsDeleteOpen} />
                </motion.div>
              </div>
            )}

            {isEmailOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center md:justify-center">
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="bg-white w-full p-6 rounded-t-2xl shadow-lg md:hidden"
                >
                  <VerifyEmailModal id={user.id} setOpen={setIsEmailOpen} />
                </motion.div>

                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="hidden md:block bg-white w-full md:w-1/3 p-6 rounded-2xl shadow-lg"
                >
                  <VerifyEmailModal id={user.id} setOpen={setIsEmailOpen} />
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
